import React, { useEffect, useMemo, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  ComposedModal,
  ContentSwitcher,
  Loading,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Switch,
} from "@carbon/react";
import i18next from "../../../i18n";
import {
  CertificationRequest,
  MaterialType,
  SiteResponse,
  TagResponse,
  UserCompanyDetail,
} from "../../../types";
import {
  capitalize,
  MEASUREMENT_UNIT_LIST,
  TO_BE_AGREED,
} from "../../../utils";
import TextField from "../../../components/carbon/Form/TextField";
import SelectField, {
  SelectFieldOption,
} from "../../../components/carbon/Form/SelectField";
import TextareaField from "../../../components/carbon/Form/TextareaField";
import MaterialFileUpload from "../../../components/NewMaterialForm/MaterialFileUpload";
import ServiceItemNotice from "../../../components/NewMaterialForm/ServiceItemNotice";
import NewMaterialAdvanced from "./NewMaterialAdvanced";
import { defaultOwnerId } from "./defaultOwner";
import {
  NewMaterialModalValues,
  newMaterialModalGoodsSchema,
  newMaterialModalServiceSchema,
} from "./newMaterialModalSchema";
import "./newMaterialModal.scss";

export type MaterialCreationType = "GOOD" | "SERVICE";

export type NewMaterialModalProps = {
  open: boolean;
  onClose: () => void;
  /** Whatever the requester typed in the search box, if they came from there. */
  initialName: string;
  buyerUsers: UserCompanyDetail[];
  sites: SiteResponse[];
  isCreating: boolean;
  isUploadingSpec: boolean;
  specFileURL: string | null;
  onUploadSpec: (file: File) => void;
  onClearSpec: () => void;
  onSubmit: (payload: any, creationType: MaterialCreationType) => void;
  onValidationError: (message: string) => void;
};

const emptyValues = (name = ""): NewMaterialModalValues => ({
  ean: "",
  globalMaterialName: name,
  ownerId: "",
  measurementUnit: "",
  customMeasurementUnit: "",
  deliveryPlace: "",
  details: "",
  brand: "",
  deliveryFormat: "",
  comments: "",
});

// De-dupe by name (two "M" entries) to avoid colliding option keys.
const unitOptions: SelectFieldOption[] = Array.from(
  new Map((MEASUREMENT_UNIT_LIST ?? []).map((u) => [u.name, u.name])).keys()
).map((name) => ({ value: name, label: name }));

/**
 * The inline "create material" modal reached from the New-Purchase-Request composer / per-item
 * autocomplete, rebuilt on Carbon's `ComposedModal` + RHF/Yup (`newMaterialModalSchema`). The
 * `POST /material` payload it builds is byte-for-byte the pre-Carbon modal's payload — only the
 * inputs (RHF fields instead of `useState`) and chrome (Carbon) changed. `tags`/`certifications`/
 * confidentiality stay parent-owned local state (no Yup rule ever governed them); the spec file
 * upload is bridged through the same `useFileInputUploader`-backed props as before.
 */
const NewMaterialModal: React.FC<NewMaterialModalProps> = ({
  open,
  onClose,
  initialName,
  buyerUsers,
  sites,
  isCreating,
  isUploadingSpec,
  specFileURL,
  onUploadSpec,
  onClearSpec,
  onSubmit,
  onValidationError,
}) => {
  const [creationType, setCreationType] =
    useState<MaterialCreationType>("GOOD");
  const [tags, setTags] = useState<TagResponse[]>([]);
  const [certifications, setCertifications] = useState<CertificationRequest[]>(
    []
  );
  const [materialConfidential, setMaterialConfidential] = useState(false);
  const [isSpecConfidential, setIsSpecConfidential] = useState(false);

  const isService = creationType === "SERVICE";

  const validationSchema = isService
    ? newMaterialModalServiceSchema
    : newMaterialModalGoodsSchema;

  const formOptions = useMemo(
    () => ({
      resolver: yupResolver(validationSchema),
      defaultValues: emptyValues(initialName),
    }),
    [validationSchema, initialName]
  );

  const { control, handleSubmit, reset } =
    useForm<NewMaterialModalValues>(formOptions);

  const measurementUnit = useWatch({ control, name: "measurementUnit" });
  const showCustomUnit =
    measurementUnit === i18next.t("measurementUnits.other");

  // Re-seed on every open: the name comes from the search box (empty when the requester started
  // from the standalone "create" button) and the owner from the company's shape.
  useEffect(() => {
    if (!open) return;
    setCreationType("GOOD");
    setTags([]);
    setCertifications([]);
    setMaterialConfidential(false);
    setIsSpecConfidential(false);
    reset({
      ...emptyValues(initialName),
      ownerId: defaultOwnerId(buyerUsers),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialName]);

  const deliveryPlaceOptions: SelectFieldOption[] = [
    ...sites.map((site) => ({ value: site.id, label: site.name })),
    { value: TO_BE_AGREED, label: TO_BE_AGREED },
  ];

  const ownerOptions: SelectFieldOption[] = buyerUsers.map((buyer) => ({
    value: buyer.id,
    label: buyer.fullName,
  }));

  const onValid = (values: NewMaterialModalValues) => {
    const finalUnit = showCustomUnit
      ? values.customMeasurementUnit
      : values.measurementUnit;

    onSubmit(
      {
        ean: values.ean.trim(),
        globalMaterialName: capitalize(values.globalMaterialName.trim()) || "",
        type: isService ? MaterialType.SERVICES : MaterialType.DIRECT_GOODS,
        measurementUnit: isService ? "" : finalUnit,
        deliveryPlace:
          isService || values.deliveryPlace === TO_BE_AGREED
            ? null
            : { id: values.deliveryPlace },
        details: values.details.trim(),
        deliveryFormat: capitalize(values.deliveryFormat) || "",
        brand: capitalize(values.brand, true) || "",
        comments: capitalize(values.comments, true) || "",
        fileURL: specFileURL || "",
        confidentSpec: isSpecConfidential,
        confident: materialConfidential,
        certifications,
        tags: tags.map((tag) => ({ id: tag.id })),
        owner: { id: values.ownerId },
        user: { id: values.ownerId },
        async: true,
      },
      creationType
    );
  };

  const title = i18next.t("purchaseRequests.items.createMaterialTitle");

  return (
    <ComposedModal
      open={open}
      aria-label={title}
      size="lg"
      onClose={() => {
        onClose();
      }}
    >
      <ModalHeader title={title} />
      <ModalBody hasForm>
        {isCreating ? (
          <div className="new-material-modal__loading">
            <Loading withOverlay={false} />
          </div>
        ) : (
          <>
            <p className="new-material-modal__intro">
              {i18next.t("purchaseRequests.items.createMaterialIntro")}
            </p>

            <div className="new-material-modal__type">
              <p className="cds--label">{i18next.t("newMaterialForm.type")}</p>
              <ContentSwitcher
                selectedIndex={isService ? 1 : 0}
                onChange={({ index }) =>
                  setCreationType(index === 1 ? "SERVICE" : "GOOD")
                }
              >
                <Switch name="GOOD" text={i18next.t("materialTypes.goods")} />
                <Switch
                  name="SERVICE"
                  text={i18next.t("materialTypes.services")}
                />
              </ContentSwitcher>
            </div>

            {isService && <ServiceItemNotice />}

            <div className="new-material-modal__grid">
              <TextField
                name="ean"
                label={
                  isService
                    ? i18next.t("newMaterialForm.eanService")
                    : i18next.t("newMaterialForm.ean")
                }
                control={control}
              />
              <TextField
                name="globalMaterialName"
                label={
                  isService
                    ? i18next.t("newMaterialForm.nameService")
                    : i18next.t("newMaterialForm.name")
                }
                control={control}
              />

              {!isService && (
                <>
                  <SelectField
                    name="measurementUnit"
                    label={i18next.t("newMaterialForm.measurementUnit")}
                    control={control}
                    options={unitOptions}
                    placeholder={i18next.t(
                      "newMaterialForm.selectMeasurementUnit"
                    )}
                  />
                  {showCustomUnit && (
                    <TextField
                      name="customMeasurementUnit"
                      label={i18next.t("newMaterialForm.customMeasurementUnit")}
                      control={control}
                      placeholder={i18next.t(
                        "newMaterialForm.enterCustomMeasurementUnit"
                      )}
                    />
                  )}
                  <SelectField
                    name="deliveryPlace"
                    label={i18next.t("newMaterialForm.deliveryPlace")}
                    control={control}
                    options={deliveryPlaceOptions}
                    placeholder={i18next.t(
                      "newMaterialForm.selectDeliveryPlace"
                    )}
                  />
                </>
              )}

              <SelectField
                name="ownerId"
                label={i18next.t("purchaseRequests.items.materialBuyerLabel")}
                control={control}
                options={ownerOptions}
                placeholder={i18next.t("newMaterialForm.selectOwner")}
                helperText={i18next.t(
                  "purchaseRequests.items.materialBuyerHint"
                )}
                className="new-material-modal__span-all"
              />

              <TextareaField
                name="details"
                label={i18next.t("purchaseRequests.items.materialDetailsLabel")}
                control={control}
                optional
                rows={3}
                enableCounter
                maxCount={255}
                placeholder={i18next.t(
                  "purchaseRequests.items.materialDetailsPlaceholder"
                )}
                className="new-material-modal__span-all"
              />
            </div>

            <MaterialFileUpload
              fileName={
                specFileURL ? i18next.t("newMaterialForm.fileUploaded") : null
              }
              isUploading={isUploadingSpec}
              onFileSelected={onUploadSpec}
              onFileRemoved={onClearSpec}
              isSpecConfidential={isSpecConfidential}
              onSpecConfidentialChange={setIsSpecConfidential}
            />

            <NewMaterialAdvanced
              control={control}
              isService={isService}
              tags={tags}
              onTagsChange={setTags}
              certifications={certifications}
              onCertificationsChange={setCertifications}
              materialConfidential={materialConfidential}
              onMaterialConfidentialChange={setMaterialConfidential}
            />
          </>
        )}
      </ModalBody>
      <ModalFooter
        secondaryButtonText={i18next.t("cta.cancel")}
        primaryButtonText={i18next.t("cta.add")}
        primaryButtonDisabled={isCreating || isUploadingSpec}
        onRequestSubmit={handleSubmit(onValid, () =>
          onValidationError(i18next.t("newMaterialForm.fillRequired"))
        )}
      >
        {null}
      </ModalFooter>
    </ComposedModal>
  );
};

export default NewMaterialModal;
